import { Link } from "react-router-dom";

import { desde, horasEmProsa } from "../api.ts";
import { Barras, Cartao, Medidor, Selo, SeloSeveridade, Vazio } from "../componentes/basicos.tsx";
import { Serie } from "../componentes/serie.tsx";
import { useDados } from "../usarDados.ts";

type Resumo = {
  avisosDoSistema: string[];
  tiles: { valor: number; rotulo: string; sub: string; alerta?: boolean }[];
  alertas: {
    id: number; tipo: string; severidade: string; titulo: string;
    empresa: string; hostname: string; aberto_em: string; licenca_id: number | null;
  }[];
  silenciosas: {
    maquina_id: number; hostname: string; empresa: string; licenca: string;
    silencio_horas: number; tolerancia_h: number; modo: string; versao: string; razao: number;
  }[];
  ocupacao: {
    vagasUsadas: number; vagasTotal: number;
    instanciasPico: number; instanciasTotal: number; licencasAcimaDoContrato: number;
  };
  sinal: { dia: string; valor: number }[];
  silencio: { rotulo: string; total: number; filtro: string }[];
  vencimentos: { rotulo: string; total: number; filtro: string }[];
  perpetuas: number;
};

export const ROTULO_TIPO: Record<string, string> = {
  "maquina.sem_contato": "Sem contato",
  "maquina.vaga_ocupada": "Vaga ocupada",
  "maquina.fingerprint_divergente": "Digital divergente",
  "maquina.disco_baixo": "Disco baixo",
  "licenca.vencendo": "Vencendo",
  "licenca.vencida": "Vencida",
  "licenca.revogada_ativa": "Revogada em uso",
};

/**
 * O painel responde duas perguntas lado a lado. Elas convivem por SEMANTICA
 * TEMPORAL, nao por adjacencia:
 *
 *   esquerda = filas que devem esvaziar       (tem verbo, vao a zero)
 *   direita  = distribuicoes que nao esvaziam (estado do mundo)
 *
 * O que nao se classifica nos dois lados nao entra. E essa regra que impede a
 * tela virar sopa quando alguem quiser acrescentar "so mais um numero".
 */
export function Painel() {
  const { dados: r, erro, carregando } = useDados<Resumo>("/resumo");

  if (carregando) return <div className="carregando">carregando...</div>;
  if (erro || !r) return <div className="alerta alerta--erro">{erro}</div>;

  return (
    <>
      <h1>Painel</h1>

      {r.avisosDoSistema.map((a) => (
        <div key={a} className="alerta alerta--aviso">
          <span className="alerta__titulo">Estado do sistema</span>{a}
        </div>
      ))}

      <div className="tiles">
        {r.tiles.map((t) => (
          <div key={t.rotulo} className={`tile${t.alerta ? " tile--alerta" : ""}`}>
            <div className="tile__valor">{t.valor}</div>
            <div className="tile__rotulo">{t.rotulo}</div>
            <div className="tile__sub">{t.sub}</div>
          </div>
        ))}
      </div>

      <div className="grade grade--painel">
        <div>
          <Cartao
            titulo="Alertas abertos"
            acoes={<Link className="botao botao--secundario botao--pequeno" to="/alertas">ver todos</Link>}
          >
            {r.alertas.length === 0 ? (
              <Vazio titulo="Nada pendente">Nenhum alerta aberto.</Vazio>
            ) : (
              <div className="tabela-rolagem">
                <table className="tabela">
                  <thead>
                    <tr><th>Gravidade</th><th>Tipo</th><th>Alerta</th><th>Empresa</th><th>Aberto</th></tr>
                  </thead>
                  <tbody>
                    {r.alertas.map((a) => (
                      <tr key={a.id}>
                        <td><SeloSeveridade severidade={a.severidade} /></td>
                        <td className="pequeno">{ROTULO_TIPO[a.tipo] ?? a.tipo}</td>
                        <td>
                          {a.licenca_id
                            ? <Link to={`/licencas/${a.licenca_id}`}>{a.titulo}</Link>
                            : a.titulo}
                        </td>
                        <td className="suave pequeno">{a.empresa}</td>
                        <td className="suave pequeno">{desde(a.aberto_em)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Cartao>

          <Cartao
            titulo="Maquinas silenciosas"
            pergunta="ordenadas pelo excesso sobre a tolerancia da licenca, nao por dias corridos"
          >
            {r.silenciosas.length === 0 ? (
              <Vazio titulo="Frota inteira falando">
                Toda maquina ativa deu sinal dentro da tolerancia.
              </Vazio>
            ) : (
              <div className="tabela-rolagem">
                <table className="tabela">
                  <thead>
                    <tr>
                      <th>Computador</th><th>Empresa</th><th>Calada ha</th>
                      <th>Tolerancia</th><th>Modo</th><th>Versao</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.silenciosas.map((m) => (
                      <tr key={m.maquina_id}>
                        <td><Link to={`/maquinas/${m.maquina_id}`}>{m.hostname}</Link></td>
                        <td className="suave pequeno">{m.empresa}</td>
                        <td>
                          {horasEmProsa(m.silencio_horas)}
                          <span className="apagado micro"> · {m.razao.toFixed(1)}x</span>
                        </td>
                        <td className="suave pequeno">{horasEmProsa(m.tolerancia_h)}</td>
                        <td>
                          {m.modo === "bloquear"
                            ? <Selo tom="critico">bloquear</Selo>
                            : <span className="apagado pequeno">avisar</span>}
                        </td>
                        <td className="suave pequeno">{m.versao}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Cartao>
        </div>

        <div>
          <Cartao titulo="Ocupacao contratada">
            <div className="pilha" style={{ gap: "var(--e-lg)" }}>
              <Medidor rotulo="Vagas de maquina" usado={r.ocupacao.vagasUsadas}
                       total={r.ocupacao.vagasTotal} unidade="vagas" />
              <Medidor rotulo="Instancias" usado={r.ocupacao.instanciasPico}
                       total={r.ocupacao.instanciasTotal} unidade=""
                       nota="no pico dos ultimos 7 dias" />
            </div>
            {r.ocupacao.licencasAcimaDoContrato > 0 && (
              <p className="pequeno mt-lg mb-0">
                <Link to="/uso">
                  {r.ocupacao.licencasAcimaDoContrato} licenca(s) acima do contratado
                </Link>
              </p>
            )}
          </Cartao>

          <Cartao titulo="Sinal da frota" pergunta="maquinas distintas que reportaram por dia">
            <Serie pontos={r.sinal} rotuloValor="Maquinas" />
          </Cartao>

          <Cartao titulo="Distribuicao de silencio">
            <Barras fatias={r.silencio.map((b) => ({ rotulo: b.rotulo, total: b.total }))} />
            <p className="micro apagado mt-lg mb-0">
              <Link to="/maquinas?silencio=30d%2B">ver as caladas ha mais de 30 dias</Link>
            </p>
          </Cartao>

          <Cartao titulo="Curva de vencimentos">
            <Barras fatias={r.vencimentos.map((b) => ({ rotulo: b.rotulo, total: b.total }))}
                    critico="vencidas" />
            {/* Perpetuas nao entram no eixo: nao tem data, nao pertencem a uma
                escala temporal. Viram texto. */}
            <p className="micro apagado mt-lg mb-0">
              {r.perpetuas} perpetua(s), sem validade — fora do eixo
            </p>
          </Cartao>
        </div>
      </div>
    </>
  );
}
